import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { getIcon } from '../../utils/IconHandler';
import { formatDate } from '../../utils/DateUtils';
import { formatMoney } from '../../utils/currency';

const headerCellSx = { backgroundColor: 'background.paper', py: 1.5 };

// Cuenta corriente como un extracto: cargos, pagos y saldo acumulado, paginado en el backend.
// `subject` identifica de quién es la cuenta (cambia → se recarga) y `fetchMovements(page, pageSize)`
// devuelve { movements, totalCount, currentBalance }.
const AccountMovementsModal = ({ open, onClose, subject, subjectName, fetchMovements, methodLabels = {} }) => {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setRows(null);
    setTotal(0);
    setPage(0);
  }, [subject, open]);

  useEffect(() => {
    if (!open || !subject) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadError(false);
    fetchMovements(page + 1, rowsPerPage)
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        setRows(response.movements || []);
        setTotal(response.totalCount || 0);
        setCurrentBalance(response.currentBalance || 0);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setLoadError(true);
        setRows([]);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [open, subject, page, rowsPerPage]);

  const changingPage = loading && rows !== null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('movements')}
          {`Movimientos${subjectName ? `: ${subjectName}` : ''}`}
        </Box>
        {rows && (
          <Typography variant="body1">
            Saldo actual: <strong style={{ color: currentBalance > 0 ? '#d32f2f' : '#2e7d32' }}>{formatMoney(currentBalance)}</strong>
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading && rows === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} /></Box>
        ) : loadError ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">No se pudieron cargar los movimientos</Typography>
          </Paper>
        ) : rows && rows.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">Este proveedor no tiene movimientos</Typography>
          </Paper>
        ) : (
          <Paper variant="outlined">
            <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}><span className="roboto-regular">Fecha</span></TableCell>
                    <TableCell sx={headerCellSx}><span className="roboto-regular">Tipo</span></TableCell>
                    <TableCell sx={headerCellSx}><span className="roboto-regular">Concepto</span></TableCell>
                    <TableCell align="center" sx={headerCellSx}><span className="roboto-regular">Forma de pago</span></TableCell>
                    <TableCell align="center" sx={headerCellSx}><span className="roboto-regular">Cargos</span></TableCell>
                    <TableCell align="center" sx={headerCellSx}><span className="roboto-regular">Pagos</span></TableCell>
                    <TableCell align="right" sx={headerCellSx}><span className="roboto-regular">Saldo</span></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changingPage && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 2 }}><CircularProgress size={24} /></TableCell>
                    </TableRow>
                  )}
                  {(rows || []).map((movement) => {
                    const isPayment = movement.type === 'PAYMENT';
                    return (
                      <TableRow
                        key={movement.id}
                        hover
                        sx={{ opacity: changingPage ? 0.4 : 1, transition: 'opacity 0.15s' }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 1.5 }}>
                          <span className="roboto-light">{formatDate(movement.date)}</span>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={isPayment ? 'Pago' : 'Factura'}
                            color={isPayment ? 'success' : 'warning'}
                            sx={{ minWidth: 72 }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}><span className="roboto-light">{movement.concept}</span></TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <span className="roboto-light">{methodLabels[movement.paymentMethod] || '-'}</span>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap', py: 1.5 }}>
                          <span className="roboto-light">{movement.charge > 0 ? formatMoney(movement.charge) : '-'}</span>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap', py: 1.5, color: movement.payment > 0 ? 'success.main' : undefined }}>
                          <span className="roboto-light">{movement.payment > 0 ? formatMoney(movement.payment) : '-'}</span>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 1.5 }}>
                          <span className="roboto-regular">{formatMoney(movement.balance)}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[20, 50, 100]}
              labelRowsPerPage="Filas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountMovementsModal;
